import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, AlertCircle, TrendingUp } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { ar, fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { Ingredient, Inventory, MeasurementUnit } from '@/types/entities';
import { useAddReceipt } from '@/hooks/useInventoryReceipts';
import { MEASUREMENT_UNITS } from '@/lib/unit-utils';

interface AddReceiptDialogProps {
  restaurantId: string;
  ingredient: Ingredient;
  currentInventory?: Inventory;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddReceiptDialog({
  restaurantId,
  ingredient,
  currentInventory,
  open,
  onOpenChange,
  onSuccess,
}: AddReceiptDialogProps) {
  const { t, i18n } = useTranslation();
  // Initialize with today's date at midnight to avoid timezone issues
  const getTodayAtMidnight = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };
  const [receiptDate, setReceiptDate] = useState<Date>(getTodayAtMidnight());
  const [quantityReceived, setQuantityReceived] = useState('');
  const [unit, setUnit] = useState<MeasurementUnit>(currentInventory?.unit || 'kg');
  const [costPerUnit, setCostPerUnit] = useState('');
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');

  const { mutate: addReceipt, isPending } = useAddReceipt(restaurantId);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Create a new date at midnight local time to avoid timezone issues
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setReceiptDate(today);
      setQuantityReceived('');
      setUnit(currentInventory?.unit || 'kg');
      setCostPerUnit('');
      setSupplier('');
      setInvoiceNumber('');
      setNotes('');
    }
  }, [open, currentInventory]);

  // Safety check - close dialog if ingredient is not available
  if (!ingredient) {
    return null;
  }

  const handleSubmit = () => {
    const qty = parseFloat(quantityReceived);
    const cost = parseFloat(costPerUnit);

    if (isNaN(qty) || qty <= 0) {
      return;
    }
    if (isNaN(cost) || cost < 0) {
      return;
    }

    addReceipt(
      {
        ingredientId: ingredient.id,
        quantityReceived: qty,
        unit,
        costPerUnit: cost,
        receiptDate,
        supplier: supplier || undefined,
        invoiceNumber: invoiceNumber || undefined,
        notes: notes || undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  const totalCost =
    quantityReceived && costPerUnit ? parseFloat(quantityReceived) * parseFloat(costPerUnit) : 0;

  const newStock = currentInventory
    ? currentInventory.quantity + (parseFloat(quantityReceived) || 0)
    : parseFloat(quantityReceived) || 0;

  const unitsDiffer = currentInventory && currentInventory.unit !== unit;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">{ingredient.emoji}</span>
            {t('inventory.addReceiptDialog.title')} -{' '}
            {i18n.language === 'ar' ? ingredient.nameAr : ingredient.nameFr}
          </DialogTitle>
          <DialogDescription>{t('inventory.addReceiptDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Receipt Date */}
            <div className="space-y-2">
              <Label>{t('inventory.addReceiptDialog.purchaseDate')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !receiptDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon
                      className={cn('h-4 w-4', i18n.language === 'ar' ? 'ml-2' : 'mr-2')}
                    />
                    {receiptDate && isValid(receiptDate) ? (
                      format(receiptDate, 'PPP', { locale: i18n.language === 'ar' ? ar : fr })
                    ) : (
                      <span>{t('inventory.addReceiptDialog.selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  align={i18n.language === 'ar' ? 'end' : 'start'}
                >
                  <Calendar
                    mode="single"
                    selected={receiptDate}
                    onSelect={(date) => {
                      if (date) {
                        // Create a new date at midnight local time to avoid timezone issues
                        const localDate = new Date(
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate(),
                          0,
                          0,
                          0,
                          0
                        );
                        setReceiptDate(localDate);
                      }
                    }}
                    initialFocus
                    locale={i18n.language === 'ar' ? ar : fr}
                    dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                    weekStartsOn={i18n.language === 'ar' ? 6 : 1}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Quantity Received */}
            <div className="space-y-2">
              <Label htmlFor="quantity">{t('inventory.addReceiptDialog.quantityReceived')} *</Label>
              <Input
                id="quantity"
                type="number"
                step="0.001"
                min="0.001"
                value={quantityReceived}
                onChange={(e) => setQuantityReceived(e.target.value)}
                placeholder="0.000"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Unit */}
            <div className="space-y-2">
              <Label htmlFor="unit">{t('inventory.addReceiptDialog.unit')} *</Label>
              <Select value={unit} onValueChange={(v) => setUnit(v as MeasurementUnit)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEASUREMENT_UNITS.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {unitsDiffer && (
                <div className="flex items-start gap-1 text-xs text-amber-600">
                  <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>
                    {t('inventory.addReceiptDialog.unitConversionWarning')} {currentInventory.unit}
                  </span>
                </div>
              )}
            </div>

            {/* Cost Per Unit */}
            <div className="space-y-2">
              <Label htmlFor="cost">
                {t('inventory.addReceiptDialog.costPerUnit')} ({t('common.currency')}) *
              </Label>
              <Input
                id="cost"
                type="number"
                step="0.01"
                min="0"
                value={costPerUnit}
                onChange={(e) => setCostPerUnit(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* Supplier */}
          <div className="space-y-2">
            <Label htmlFor="supplier">{t('inventory.addReceiptDialog.supplier')}</Label>
            <Input
              id="supplier"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder={t('inventory.addReceiptDialog.supplierPlaceholder')}
            />
          </div>

          {/* Invoice Number */}
          <div className="space-y-2">
            <Label htmlFor="invoice">{t('inventory.addReceiptDialog.invoiceNumber')}</Label>
            <Input
              id="invoice"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder={t('inventory.addReceiptDialog.invoicePlaceholder')}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('inventory.addReceiptDialog.notes')}</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('inventory.addReceiptDialog.notesPlaceholder')}
              rows={2}
            />
          </div>

          {/* Summary */}
          {quantityReceived && costPerUnit && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('inventory.addReceiptDialog.totalCost')}:
                </span>
                <span className="font-bold text-lg">
                  {totalCost.toFixed(2)} {t('common.currency')}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('inventory.addReceiptDialog.currentStock')}:
                </span>
                <span>
                  {currentInventory?.quantity.toFixed(3) || 0} {currentInventory?.unit}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('inventory.addReceiptDialog.newStock')}:
                </span>
                <Badge variant="outline" className="gap-1">
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  {newStock.toFixed(3)} {currentInventory?.unit || unit}
                </Badge>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={
              isPending ||
              !quantityReceived ||
              parseFloat(quantityReceived) <= 0 ||
              !costPerUnit ||
              parseFloat(costPerUnit) < 0
            }
          >
            {isPending
              ? t('inventory.addReceiptDialog.adding')
              : t('inventory.addReceiptDialog.addReceipt')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
